import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  Flex,
  Spinner,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";

export type CboDispatch = {
  dispatchedQuantity?: number;
  gatePassNo?: string | null;
  gatePassDate?: string | null;
};

export type CboDocument = {
  supplierBusinessName: string;
  advancedOrderQuantity: number;
  backOrderQuantity: number;
  allocatedQuantity: number;
  underPickingQuantity: number;
  invoiceQuantity: number;
  totalCBOQuantity?: number;
  dispatchedQuantity?: number;
  dispatched: CboDispatch[];
  error: string | null;
  updatedAt: string | null;
};

export type CboGroup = {
  partNumber: string;
  documents: CboDocument[];
};

interface CboDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
  error: string | null;
  cboGroups: CboGroup[];
  partNameMap: Map<string, string>;
}

const getOrdinalDay = (day: number) => {
  if (day > 3 && day < 21) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
};

const formatDispatchDate = (value?: string | null) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  const day = getOrdinalDay(parsed.getDate());
  const month = parsed.toLocaleString("en-IN", { month: "short" });
  return `${day},${month}`;
};

const getDispatchedTotal = (row: CboDocument) =>
  (row.dispatched || []).reduce(
    (sum, dispatchItem) => sum + (dispatchItem.dispatchedQuantity || 0),
    0,
  );

const getDispatchedDisplay = (row: CboDocument) => {
  const entries = (row.dispatched || [])
    .filter(
      (dispatchItem) =>
        Boolean(dispatchItem.gatePassDate) ||
        typeof dispatchItem.dispatchedQuantity === "number",
    )
    .map((dispatchItem) => {
      const dateLabel = formatDispatchDate(dispatchItem.gatePassDate) || "Unknown";
      const quantity = dispatchItem.dispatchedQuantity || 0;
      return `${dateLabel} ~ ${quantity}`;
    });

  if (entries.length > 0) {
    return entries.join(" | ");
  }
  return "—";
};

const getCboQuantity = (row: CboDocument) =>
  row.totalCBOQuantity || 0;

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const CboDetailsModal: React.FC<CboDetailsModalProps> = ({
  isOpen,
  onClose,
  isLoading,
  error,
  cboGroups,
  partNameMap,
}) => {
  const [expandedIndexes, setExpandedIndexes] = React.useState<number[]>([]);

  React.useEffect(() => {
    if (!isOpen || cboGroups.length === 0) return;
    setExpandedIndexes(
      cboGroups
        .map((group, index) =>
          Array.isArray(group.documents) && group.documents.length > 0 ? index : null,
        )
        .filter((idx): idx is number => idx !== null)
    );
  }, [isOpen, cboGroups]);

  const handleAccordionChange = React.useCallback(
    (value: number | number[]) => {
      setExpandedIndexes(Array.isArray(value) ? value : [value]);
    },
    [],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="xl" overflow="hidden" maxH="85vh">
        <Box
          bgGradient="linear(to-r, #1976f2, #5b2be0)"
          px={6}
          py={4}
          position="relative"
        >
          <ModalCloseButton
            color="white"
            top={3}
            right={4}
            _hover={{ bg: "white", color: "blue.500" }}
          />
          <Text fontSize="lg" fontWeight="bold" color="white">
            CBO Details by Part Number
          </Text>
          <Text fontSize="sm" fontWeight="500" color="rgba(255, 255, 255, 0.64)">
            Consolidated breakdown for remaining parts
          </Text>
        </Box>

        <ModalBody
          border="1px solid"
          borderColor="gray.200"
          borderRadius="lg"
          boxShadow="xs"
          bg="white"
          overflowY="auto"
        >
          {isLoading ? (
            <Flex justify="center" py={10}>
              <Spinner color="blue.500" />
            </Flex>
          ) : error ? (
            <Text color="red.500" fontSize="sm">
              {error}
            </Text>
          ) : cboGroups.length === 0 ? (
            <Text color="gray.600" fontSize="sm">
              No CBO records found for selected part numbers.
            </Text>
          ) : (
            <Accordion
              allowMultiple
              index={expandedIndexes}
              onChange={handleAccordionChange}
              border="none"
            >
              {cboGroups.map((group) => {
                const totals = group.documents.reduce(
                  (acc, row) => ({
                    cbo: acc.cbo + getCboQuantity(row),
                    dispatched: acc.dispatched + getDispatchedTotal(row),
                    invoiced: acc.invoiced + (row.invoiceQuantity || 0),
                    underPicking:
                      acc.underPicking + (row.underPickingQuantity || 0),
                    allocated: acc.allocated + (row.allocatedQuantity || 0),
                    backOrder: acc.backOrder + (row.backOrderQuantity || 0),
                    advanceOrder:
                      acc.advanceOrder + (row.advancedOrderQuantity || 0),
                  }),
                  {
                    cbo: 0,
                    dispatched: 0,
                    invoiced: 0,
                    underPicking: 0,
                    allocated: 0,
                    backOrder: 0,
                    advanceOrder: 0,
                  },
                );

                return (
                  <AccordionItem
                    key={group.partNumber}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    mb={3}
                    overflow="hidden"
                  >
                    <h2>
                      <AccordionButton
                        bg="gray.50"
                        _hover={{ bg: "gray.100" }}
                        py={3}
                      >
                        <Flex flex="1" align="center" gap={3}>
                          <Text fontWeight="800" fontSize="sm" color="gray.700">
                            {group.partNumber}
                          </Text>
                          <Text fontWeight="500" fontSize="sm" color="gray.500">
                            {partNameMap.get(group.partNumber) || ""}
                          </Text>
                        </Flex>
                        <Text fontSize="xs" color="gray.500" mr={3}>
                          {group.documents.length} Suppliers
                        </Text>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel p={0}>
                      {group.documents.length === 0 ? (
                        <Text
                          fontSize="sm"
                          color="gray.500"
                          px={4}
                          py={4}
                          fontWeight="600"
                        >
                          CBO data not found for this part.
                        </Text>
                      ) : (
                        <>
                          <Box overflowX="auto">
                            <Table size="sm" variant="unstyled">
                              <Thead bg="gray.100">
                                <Tr>
                                  <Th textAlign="left">Supplier Name</Th>
                                  <Th textAlign="center">CBO</Th>
                                  <Th textAlign="center">Dispatched</Th>
                                  <Th textAlign="center">Invoiced</Th>
                                  <Th textAlign="center">Under Picking</Th>
                                  <Th textAlign="center">Allocated</Th>
                                  <Th textAlign="center">Back Order</Th>
                                  <Th textAlign="center">Advance Order</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {group.documents.map((row, rowIndex) => (
                                  <Tr
                                    key={`${group.partNumber}-${row.supplierBusinessName}-${rowIndex}`}
                                    bg={
                                      rowIndex % 2 === 0 ? "white" : "gray.50"
                                    }
                                  >
                                    <Td fontWeight="700" color="gray.700">
                                      {row.supplierBusinessName}
                                    </Td>
                                    <Td
                                      textAlign="center"
                                      fontWeight="700"
                                      color="#2F9E77"
                                      fontSize="xs"
                                    >
                                      {getCboQuantity(row) || "—"}
                                    </Td>
                                    <Td textAlign="center" fontSize="xs">
                                      {getDispatchedDisplay(row)}
                                    </Td>
                                    <Td textAlign="center">
                                      {row.invoiceQuantity || 0}
                                    </Td>
                                    <Td textAlign="center">
                                      {row.underPickingQuantity || 0}
                                    </Td>
                                    <Td textAlign="center">
                                      {row.allocatedQuantity || 0}
                                    </Td>
                                    <Td
                                      textAlign="center"
                                      color="#C95A8C"
                                      fontWeight="700"
                                    >
                                      {row.backOrderQuantity || 0}
                                    </Td>
                                    <Td
                                      textAlign="center"
                                      color="#64AFA0"
                                      fontWeight="700"
                                    >
                                      {row.advancedOrderQuantity || 0}
                                    </Td>
                                  </Tr>
                                ))}
                                <Tr bg="gray.100">
                                  <Td fontWeight="800">Total</Td>
                                  <Td
                                    textAlign="center"
                                    fontWeight="800"
                                    color="#2F9E77"
                                  >
                                    {totals.cbo || "—"}
                                  </Td>
                                  <Td textAlign="center" fontWeight="800">
                                    {totals.dispatched}
                                  </Td>
                                  <Td textAlign="center" fontWeight="800">
                                    {totals.invoiced}
                                  </Td>
                                  <Td
                                    textAlign="center"
                                    fontWeight="800"
                                    color="#A8792A"
                                  >
                                    {totals.underPicking}
                                  </Td>
                                  <Td textAlign="center" fontWeight="800">
                                    {totals.allocated}
                                  </Td>
                                  <Td
                                    textAlign="center"
                                    fontWeight="800"
                                    color="#C95A8C"
                                  >
                                    {totals.backOrder}
                                  </Td>
                                  <Td
                                    textAlign="center"
                                    fontWeight="800"
                                    color="#64AFA0"
                                  >
                                    {totals.advanceOrder}
                                  </Td>
                                </Tr>
                              </Tbody>
                            </Table>
                          </Box>
                          <Text
                            fontSize="xs"
                            color="gray.400"
                            px={4}
                            pb={3}
                            textAlign="right"
                          >
                            Updated:{" "}
                            {formatDateTime(
                              group.documents
                                .map((doc) => doc.updatedAt)
                                .filter(Boolean)
                                .sort()
                                .reverse()[0],
                            )}
                          </Text>
                        </>
                      )}
                    </AccordionPanel>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CboDetailsModal;
